import { SHORT_MONTHS } from "./constants";

export const scrollToSection = (id: any) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

export const sortData = (
  a: any,
  b: any,
  orderBy: string,
  order: "asc" | "desc"
): number => {
  const valA = a[orderBy];
  const valB = b[orderBy];

  if (valA === null || valA === undefined) return order === "asc" ? 1 : -1;
  if (valB === null || valB === undefined) return order === "asc" ? -1 : 1;

  if (typeof valA === "string") {
    return order === "asc"
      ? valA.localeCompare(valB)
      : valB.localeCompare(valA);
  } else if (typeof valA === "number") {
    return order === "asc" ? valA - valB : valB - valA;
  } else if (typeof valA === "boolean") {
    return order === "asc"
      ? valA === valB
        ? 0
        : valA
          ? 1
          : -1
      : valA === valB
        ? 0
        : valA
          ? -1
          : 1;
  } else if (valA instanceof Date) {
    return order === "asc"
      ? //@ts-ignore
        new Date(valA) - new Date(valB)
      : //@ts-ignore
        new Date(valB) - new Date(valA);
  } else {
    return 0;
  }
};
export const scrollToSectionMobile = (id: any) => {
  const element = document.getElementById(id);
  if (element) {
    const yOffset = -20;
    const y =
      element.getBoundingClientRect().top + window.pageYOffset + yOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  }
};

export const scrollToTop = (ref: any) => {
  ref?.current?.scroll({
    top: 0,
    behavior: "smooth",
  });
};

export function getObjectById(
  idToFind: any,
  arrayOfObjects: any[]
): any | undefined {
  if (!arrayOfObjects || !arrayOfObjects?.length) return undefined;
  for (const obj of arrayOfObjects) {
    if (obj.id === idToFind) {
      return obj;
    }
  }
  return undefined;
}

export function getObjectBySubId(
  idToFind: any,
  arrayOfObjects: any[]
): any | undefined {
  if (!arrayOfObjects || !arrayOfObjects?.length) return undefined;
  for (const obj of arrayOfObjects) {
    if (obj.subscriptionId === idToFind) {
      return obj;
    }
  }
  return undefined;
}

export function getDaysBetweenDates(
  startDate: string | null,
  endDate: string | null
): string | null {
  if (startDate === null || endDate === null) {
    return "";
  }
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffInTime = end.getTime() - start.getTime();
  const diffInDays = diffInTime / (1000 * 3600 * 24);
  return Math.abs(Math.round(diffInDays)) + "";
}

export function convertDateTime(input: string | null): string {
  if (!input) return "";
  const date = new Date(input);
  const day = date.getDate();
  const month = SHORT_MONTHS[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

export const slugify = (input: string): string => {
  if (!input || !input.length) return "";
  return (
    input
      .toLowerCase()
      .trim()
      // Replace anything that is not a letter, digit, space, or hyphen
      .replace(/[^\p{L}\p{N}\s-]/gu, "")
      .replace(/\s+/g, "-")
      .replace(/--+/g, "-")
  );
};

export const deslugify = (slug: string): string => {
  if (!slug || !slug.length) return "";
  return slug.replace(/-/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

export const parseJsonBody = (jsonBody: string | null | undefined) => {
  if (!jsonBody) return {};
  try {
    return JSON.parse(jsonBody);
  } catch {
    return {};
  }
};
export function convertSlateContentGeneral(
  pagesData: any[][],
  companyData: Record<string, any>
): any[][] {
  return pagesData.map((pageContent) =>
    pageContent.flatMap((node) => {
      if (node.type === "editor" && Array.isArray(node.content)) {
        return node.content.flatMap((contentNode: any) => {
          const processed = processNode(contentNode, companyData);
          return processed
            ? Array.isArray(processed)
              ? processed
              : [processed]
            : [];
        });
      }
      const processed = processNode(node, companyData);
      return processed
        ? Array.isArray(processed)
          ? processed
          : [processed]
        : [];
    })
  );
}

/**
 * Recursively process a Slate node.
 *
 * The changes include:
 * - If the node (or its sole child) is exactly a placeholder (e.g. "{{company_pricing}}")
 *   and the corresponding companyData value is an array, then the node is replaced with
 *   the array's contents.
 * - Child nodes are processed using a flat mapping approach to ensure any arrays returned are flattened.
 */
function processNode(node: any, companyData: Record<string, any>): any {
  if (typeof node !== "object" || node === null) return node;

  // If the node itself does not have a direct "text" property but has one child,
  // check if that sole child's text exactly matches a placeholder.
  if (
    !node.text &&
    Array.isArray(node.children) &&
    node.children.length === 1 &&
    node.children[0] &&
    typeof node.children[0].text === "string"
  ) {
    const placeholderText = node.children[0].text;
    const regexExact = /^{{(.*?)}}$/;
    const match = placeholderText.match(regexExact);
    if (match) {
      const key = match[1].trim();
      if (companyData[key] !== undefined) {
        const value = companyData[key];
        if (Array.isArray(value)) {
          return value; // Replace the entire node with the array content.
        } else {
          // Otherwise, replace the placeholder text.
          // node.children[0].text = String(value);
        }
      }
    }
  }

  // Clone the node to avoid mutating the original.
  const processedNode = { ...node };

  // If this node has a direct text property, check for placeholders.
  if (processedNode.text && typeof processedNode.text === "string") {
    const regex = /{{(.*?)}}/g;
    const matches = processedNode.text.match(regex);
    if (matches) {
      // If the entire text is exactly one placeholder, process it.
      if (matches.length === 1 && processedNode.text.trim() === matches[0]) {
        const key = matches[0].replace(/{{|}}/g, "").trim();
        if (companyData[key] !== undefined) {
          const value = companyData[key];
          if (Array.isArray(value)) {
            return value; // Replace this node entirely.
          }
          processedNode.text = String(value);
        }
      } else {
        // Otherwise, do an inline replacement.
        processedNode.text = processedNode.text.replace(
          regex,
          (match: any, key: any) =>
            companyData[key.trim()] !== undefined
              ? String(companyData[key.trim()])
              : match
        );
      }
    }
  }

  // Process children using flat mapping.
  if (Array.isArray(processedNode.children)) {
    const newChildren: any[] = [];
    for (const child of processedNode.children) {
      const processedChild = processNode(child, companyData);
      if (Array.isArray(processedChild)) {
        newChildren.push(...processedChild);
      } else if (processedChild !== undefined && processedChild !== null) {
        newChildren.push(processedChild);
      }
    }
    processedNode.children = newChildren;
  }

  return processedNode;
}

export interface EditorData {
  id: string;
  type: string;
  content: any;
}

/**
 * Uploads a base64 image to ImgBB and returns the URL.
 *
 * @param base64Image - The base64 image data.
 * @returns Promise with the uploaded image URL.
 */
async function uploadImageToImgBB(base64Image: string): Promise<string> {
  const apiKey =
    process.env.NEXT_PUBLIC_IMGBB_API_KEY || "your-default-api-key";
  const formData = new FormData();
  formData.append("image", base64Image.split(",")[1]);

  try {
    const response = await fetch(
      `https://api.imgbb.com/1/upload?key=${"ce9e3977e50b8db8abb637fb185158a2"}`,
      {
        method: "POST",
        body: formData,
      }
    );
    const data = await response.json();
    if (data.success) {
      return data.data.url;
    }
    throw new Error("Image upload failed");
  } catch (error) {
    console.error("Error uploading image:", error);
    throw error;
  }
}

async function blobUrlToBase64(blobUrl: string): Promise<string> {
  try {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error converting blob to base64:", error);
    throw error;
  }
}

/**
 * Processes page content and uploads any base64 images to ImgBB.
 *
 * @param pagesData - Array of page content.
 * @param coverDataList - Array of cover data.
 * @returns Promise with processed content with uploaded image URLs.
 */
export async function processAndUploadImages(
  pagesData: any[][],
  coverDataList?: any[]
): Promise<{ processedPages: any[][]; processedCover?: any[] }> {
  const processNodeForUpload = async (node: any): Promise<any> => {
    if (!node) return node;

    if (node.type === "img" && node.properties?.content) {
      const contentUrl = node.properties.content;
      if (contentUrl && contentUrl.startsWith("blob:")) {
        try {
          const base64Data = await blobUrlToBase64(contentUrl);
          const imageUrl = await uploadImageToImgBB(base64Data);
          return {
            ...node,
            properties: {
              ...node.properties,
              content: imageUrl,
            },
          };
        } catch (error) {
          console.error("Failed to upload cover image:", error);
          return node;
        }
      }
      return node;
    }

    if (node.type === "img") {
      if (node.url && node.url.startsWith("blob:")) {
        try {
          const base64Data = await blobUrlToBase64(node.url);
          const imageUrl = await uploadImageToImgBB(base64Data);
          return { ...node, url: imageUrl };
        } catch (error) {
          console.error("Failed to upload image:", error);
          return node;
        }
      }
      if (node.content) {
        const contentUrl =
          typeof node.content === "object" ? node.content.url : node.content;
        if (
          contentUrl &&
          (contentUrl.startsWith("data:") || contentUrl.startsWith("blob:"))
        ) {
          try {
            const imageUrl = await uploadImageToImgBB(contentUrl);
            return {
              ...node,
              content:
                typeof node.content === "object"
                  ? { ...node.content, url: imageUrl }
                  : imageUrl,
            };
          } catch (error) {
            console.error("Failed to upload image:", error);
            return node;
          }
        }
      }
    }

    if (node.type === "bgImage" && node.properties?.content) {
      const contentUrl = node.properties.content;
      if (contentUrl && contentUrl.startsWith("blob:")) {
        try {
          const base64Data = await blobUrlToBase64(contentUrl);
          const imageUrl = await uploadImageToImgBB(base64Data);
          return {
            ...node,
            properties: {
              ...node.properties,
              content: imageUrl,
            },
          };
        } catch (error) {
          console.error("Failed to upload background image:", error);
          return node;
        }
      }
    }

    if (node.type === "editor") {
      const processedContent = await Promise.all(
        (Array.isArray(node.content) ? node.content : []).map(
          async (item: any) => {
            if (item.type === "img") {
              return await processNodeForUpload(item);
            }
            if (item.children) {
              const processedChildren = await Promise.all(
                item.children.map(processNodeForUpload)
              );
              return { ...item, children: processedChildren };
            }
            return item;
          }
        )
      );
      return { ...node, content: processedContent };
    }

    if (Array.isArray(node.content)) {
      const processedContent = await Promise.all(
        node.content.map(processNodeForUpload)
      );
      return { ...node, content: processedContent };
    }

    if (node.children) {
      const processedChildren = await Promise.all(
        node.children.map(processNodeForUpload)
      );
      return { ...node, children: processedChildren };
    }

    return node;
  };

  let processedCover;
  if (coverDataList) {
    processedCover = await Promise.all(coverDataList.map(processNodeForUpload));
  }

  const processedPages = await Promise.all(
    pagesData.map(async (page) => {
      return await Promise.all(
        page.map(async (node: any) => {
          const processed = await processNodeForUpload(node);
          return processed
            ? Array.isArray(processed)
              ? processed
              : [processed]
            : [];
        })
      );
    })
  );

  return {
    processedPages,
    processedCover,
  };
}

export function processPagesList(
  pagesList: Array<{ id: string; title: string; data: any[] }>,
  companyData: Record<string, any>
): Array<{ id: string; title: string; data: any[] }> {
  return pagesList.map((page) => ({
    id: page.id,
    title: page.title,
    data: Array.isArray(page.data)
      ? page.data.flatMap((node) => {
          if (!node) return [];
          if (node.type !== "editor") {
            const processed = processNode(node, companyData);
            return processed
              ? Array.isArray(processed)
                ? processed
                : [processed]
              : [];
          }
          return [
            {
              ...node,
              content: Array.isArray(node.content)
                ? node.content.flatMap((contentNode: any) => {
                    const processed = processNode(contentNode, companyData);
                    return processed
                      ? Array.isArray(processed)
                        ? processed
                        : [processed]
                      : [];
                  })
                : [],
            },
          ];
        })
      : [],
  }));
}

//content block

export const uploadBlockImageToImgBB = async (
  imageData: string
): Promise<string> => {
  // Skip if already a remote URL (not blob or data)
  if (!imageData.startsWith("blob:") && !imageData.startsWith("data:")) {
    return imageData;
  }

  try {
    // Convert blob URL to base64 if needed
    let base64Data = imageData;
    if (imageData.startsWith("blob:")) {
      const response = await fetch(imageData);
      const blob = await response.blob();
      base64Data = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    }

    // Extract the base64 part without the data URL prefix
    const base64Content = base64Data.split(",")[1];

    // Create form data for upload
    const formData = new FormData();
    formData.append("image", base64Content);

    // Upload to ImgBB
    const response = await fetch(
      "https://api.imgbb.com/1/upload?key=ce9e3977e50b8db8abb637fb185158a2",
      {
        method: "POST",
        body: formData,
      }
    );

    const data = await response.json();
    if (data.success) {
      return data.data.url;
    }

    throw new Error("Image upload failed");
  } catch (error) {
    console.error("Error uploading image:", error);
    throw error;
  }
};

export const processBlockImageUrls = async (node: any): Promise<any> => {
  if (!node) return node;

  // Handle direct image nodes
  if ((node.type === "img" || node.isUpload) && node.url) {
    if (node.url.startsWith("blob:") || node.url.startsWith("data:")) {
      try {
        const imageUrl = await uploadImageToImgBB(node.url);
        return { ...node, url: imageUrl };
      } catch (error) {
        console.error("Failed to process image:", error);
      }
    }
  }

  // Create a copy to avoid modifying the original
  const result = { ...node };

  // Process children if they exist
  if (result.children && Array.isArray(result.children)) {
    result.children = await Promise.all(
      result.children.map(processBlockImageUrls)
    );
  }

  return result;
};

// Helper function to check if a node is an image that needs processing
export const isProcessableImage = (node: any): boolean => {
  return (
    node &&
    (node.type === "img" || node.isUpload) &&
    node.url &&
    (node.url.startsWith("blob:") || node.url.startsWith("data:"))
  );
};

// Deep recursive function to process any node structure
export const processNodeDeep = async (node: any): Promise<any> => {
  if (!node || typeof node !== "object") return node;

  // Create a copy of the node to avoid modifying the original
  let result = Array.isArray(node) ? [...node] : { ...node };

  // Handle image nodes
  if (isProcessableImage(node)) {
    try {
      const imageUrl = await uploadBlockImageToImgBB(node.url);
      result.url = imageUrl;
    } catch (error) {
      console.error("Failed to process image:", error);
    }
  }

  // Process all properties recursively
  for (const key in result) {
    if (Object.prototype.hasOwnProperty.call(result, key)) {
      const value = result[key];

      // Process arrays (like children)
      if (Array.isArray(value)) {
        result[key] = await Promise.all(value.map(processNodeDeep));
      }
      // Process nested objects
      else if (value && typeof value === "object") {
        result[key] = await processNodeDeep(value);
      }
    }
  }

  return result;
};

// Main function to process editor content
export const processBlockEditorContent = async (
  editorContent: any[]
): Promise<any[]> => {
  if (!editorContent || !Array.isArray(editorContent)) {
    return [];
  }
  // Process all nodes deeply
  return await Promise.all(editorContent.map(processNodeDeep));
};

/**
 * Transforms proposal data to Component format
 * @param pagesList - Array of pages with editor content
 * @param coverDataList - Array of cover data nodes
 * @param html - Generated HTML string
 * @param componentId - Optional component ID (defaults to 0)
 * @returns Component object in the new format
 */
export function transformProposalToComponent(
  pagesList: Array<{ id: string; title: string; data: any[] }>,
  coverDataList: any[],
  html: string,
  componentId: number = 0
): any {
  // Extract cover data
  const coverImageNode = coverDataList.find((node) => node.type === "img");
  const headerNode = coverDataList.find((node) => node.type === "header");
  const subHeaderNode = coverDataList.find((node) => node.type === "subHeader");

  const imageUrl = coverImageNode?.properties?.content || "";
  const title = headerNode?.properties?.content || "";
  const description = subHeaderNode?.properties?.content || "";

  // Build tree structure from pagesList
  const tree = pagesList.map((page) => ({
    id: page.id,
    title: page.title,
    data: page.data,
  }));

  // Create jsonBody with tree and html
  const jsonBodyData = {
    tree,
    html,
  };

  // Extract media from cover and content
  const media: any[] = [];

  // Add cover image to media
  if (coverImageNode?.properties?.content) {
    media.push({
      id: media.length + 1,
      componentId,
      url: coverImageNode.properties.content,
      mediaType: "image",
      image: coverImageNode.properties.content,
    });
  }

  // Extract images from content
  pagesList.forEach((page) => {
    page.data.forEach((element) => {
      if (element.type === "img" && element.content?.url) {
        media.push({
          id: media.length + 1,
          componentId,
          url: element.content.url,
          mediaType: "image",
          image: element.content.url,
        });
      } else if (element.type === "editor" && Array.isArray(element.content)) {
        element.content.forEach((node: any) => {
          if (node.type === "img" && node.url) {
            media.push({
              id: media.length + 1,
              componentId,
              url: node.url,
              mediaType: "image",
              image: node.url,
            });
          }
        });
      }
    });
  });

  return {
    id: componentId,
    lang: "en",
    parentId: 0,
    title,
    note: "",
    description,
    jsonBody: JSON.stringify(jsonBodyData),
    url: "",
    imageUrl,
    displayOrder: 0,
    media,
    image: imageUrl,
  };
}

/**
 * Generates HTML from editor content using the same logic as export-toolbar-button
 * @param editorContent - Array of editor nodes/content
 * @returns HTML string
 */
export async function generateHtmlFromEditorContent(
  editorContent: any[]
): Promise<string> {
  if (!editorContent || editorContent.length === 0) {
    return "";
  }

  try {
    // Import required modules dynamically
    const [
      { serializeHtml, createSlateEditor, BaseParagraphPlugin, SlateLeaf },
      {
        BaseBoldPlugin,
        BaseItalicPlugin,
        BaseStrikethroughPlugin,
        BaseUnderlinePlugin,
        BaseCodePlugin,
        BaseSubscriptPlugin,
        BaseSuperscriptPlugin,
      },
      { BaseHeadingPlugin, HEADING_KEYS, HEADING_LEVELS },
      { BaseLinkPlugin },
      {
        BaseImagePlugin,
        BaseVideoPlugin,
        BaseAudioPlugin,
        BaseFilePlugin,
        BaseMediaEmbedPlugin,
      },
      { BaseBlockquotePlugin },
      { BaseCodeBlockPlugin, BaseCodeLinePlugin, BaseCodeSyntaxPlugin },
      { BaseHorizontalRulePlugin },
      { BaseKbdPlugin },
      { BaseHighlightPlugin },
      { BaseDatePlugin },
      { BaseMentionPlugin },
      { BaseTogglePlugin },
      { BaseTocPlugin },
      { BaseColumnPlugin, BaseColumnItemPlugin },
      { BaseIndentPlugin },
      { BaseIndentListPlugin },
      { BaseAlignPlugin },
      {
        BaseFontColorPlugin,
        BaseFontBackgroundColorPlugin,
        BaseFontSizePlugin,
      },
      { BaseLineHeightPlugin },
      { BaseTableCellPlugin, BaseTablePlugin, BaseTableRowPlugin },
      { BaseEquationPlugin, BaseInlineEquationPlugin },
      { BaseCommentsPlugin },
      { withProps },
    ] = await Promise.all([
      import("@udecode/plate"),
      import("@udecode/plate-basic-marks"),
      import("@udecode/plate-heading"),
      import("@udecode/plate-link"),
      import("@udecode/plate-media"),
      import("@udecode/plate-block-quote"),
      import("@udecode/plate-code-block"),
      import("@udecode/plate-horizontal-rule"),
      import("@udecode/plate-kbd"),
      import("@udecode/plate-highlight"),
      import("@udecode/plate-date"),
      import("@udecode/plate-mention"),
      import("@udecode/plate-toggle"),
      import("@udecode/plate-heading"),
      import("@udecode/plate-layout"),
      import("@udecode/plate-indent"),
      import("@udecode/plate-indent-list"),
      import("@udecode/plate-alignment"),
      import("@udecode/plate-font"),
      import("@udecode/plate-line-height"),
      import("@udecode/plate-table"),
      import("@udecode/plate-math"),
      import("@udecode/plate-comments"),
      import("@udecode/cn"),
    ]);

    // Import static components
    const [
      { BlockquoteElementStatic },
      { CodeBlockElementStatic },
      { CodeLeafStatic },
      { CodeLineElementStatic },
      { CodeSyntaxLeafStatic },
      { ColumnElementStatic },
      { ColumnGroupElementStatic },
      { DateElementStatic },
      { HeadingElementStatic },
      { HighlightLeafStatic },
      { HrElementStatic },
      { ImageElementStatic },
      { KbdLeafStatic },
      { LinkElementStatic },
      { MediaAudioElementStatic },
      { MediaFileElementStatic },
      { MediaVideoElementStatic },
      { MentionElementStatic },
      { ParagraphElementStatic },
      { TocElementStatic },
      { ToggleElementStatic },
      { EditorStatic },
    ] = await Promise.all([
      import("@/components/Dashboard/plate-ui/blockquote-element-static"),
      import("@/components/Dashboard/plate-ui/code-block-element-static"),
      import("@/components/Dashboard/plate-ui/code-leaf-static"),
      import("@/components/Dashboard/plate-ui/code-line-element-static"),
      import("@/components/Dashboard/plate-ui/code-syntax-leaf-static"),
      import("@/components/Dashboard/plate-ui/column-element-static"),
      import("@/components/Dashboard/plate-ui/column-group-element-static"),
      import("@/components/Dashboard/plate-ui/date-element-static"),
      import("@/components/Dashboard/plate-ui/heading-element-static"),
      import("@/components/Dashboard/plate-ui/highlight-leaf-static"),
      import("@/components/Dashboard/plate-ui/hr-element-static"),
      import("@/components/Dashboard/plate-ui/image-element-static"),
      import("@/components/Dashboard/plate-ui/kbd-leaf-static"),
      import("@/components/Dashboard/plate-ui/link-element-static"),
      import("@/components/Dashboard/plate-ui/media-audio-element-static"),
      import("@/components/Dashboard/plate-ui/media-file-element-static"),
      import("@/components/Dashboard/plate-ui/media-video-element-static"),
      import("@/components/Dashboard/plate-ui/mention-element-static"),
      import("@/components/Dashboard/plate-ui/paragraph-element-static"),
      import("@/components/Dashboard/plate-ui/toc-element-static"),
      import("@/components/Dashboard/plate-ui/toggle-element-static"),
      import("@/components/Dashboard/plate-ui/editor-static"),
    ]);

    // Import Prism for code blocks
    //@ts-ignore
    const Prism = (await import("prismjs")).default;

    // Create components mapping (same as export-toolbar-button)
    const components = {
      [BaseAudioPlugin.key]: MediaAudioElementStatic,
      [BaseBlockquotePlugin.key]: BlockquoteElementStatic,
      [BaseBoldPlugin.key]: withProps(SlateLeaf, { as: "strong" }),
      [BaseCodeBlockPlugin.key]: CodeBlockElementStatic,
      [BaseCodeLinePlugin.key]: CodeLineElementStatic,
      [BaseCodePlugin.key]: CodeLeafStatic,
      [BaseCodeSyntaxPlugin.key]: CodeSyntaxLeafStatic,
      [BaseColumnItemPlugin.key]: ColumnElementStatic,
      [BaseColumnPlugin.key]: ColumnGroupElementStatic,
      [BaseDatePlugin.key]: DateElementStatic,
      [BaseFilePlugin.key]: MediaFileElementStatic,
      [BaseHighlightPlugin.key]: HighlightLeafStatic,
      [BaseHorizontalRulePlugin.key]: HrElementStatic,
      [BaseImagePlugin.key]: ImageElementStatic,
      [BaseItalicPlugin.key]: withProps(SlateLeaf, { as: "em" }),
      [BaseKbdPlugin.key]: KbdLeafStatic,
      [BaseLinkPlugin.key]: LinkElementStatic,
      [BaseMentionPlugin.key]: MentionElementStatic,
      [BaseParagraphPlugin.key]: ParagraphElementStatic,
      [BaseStrikethroughPlugin.key]: withProps(SlateLeaf, { as: "del" }),
      [BaseSubscriptPlugin.key]: withProps(SlateLeaf, { as: "sub" }),
      [BaseSuperscriptPlugin.key]: withProps(SlateLeaf, { as: "sup" }),
      [BaseTocPlugin.key]: TocElementStatic,
      [BaseTogglePlugin.key]: ToggleElementStatic,
      [BaseUnderlinePlugin.key]: withProps(SlateLeaf, { as: "u" }),
      [BaseVideoPlugin.key]: MediaVideoElementStatic,
      [HEADING_KEYS.h1]: withProps(HeadingElementStatic, { variant: "h1" }),
      [HEADING_KEYS.h2]: withProps(HeadingElementStatic, { variant: "h2" }),
      [HEADING_KEYS.h3]: withProps(HeadingElementStatic, { variant: "h3" }),
      [HEADING_KEYS.h4]: withProps(HeadingElementStatic, { variant: "h4" }),
      [HEADING_KEYS.h5]: withProps(HeadingElementStatic, { variant: "h5" }),
      [HEADING_KEYS.h6]: withProps(HeadingElementStatic, { variant: "h6" }),
    };

    // Create editor instance with all plugins (same as export-toolbar-button)
    const editorStatic = createSlateEditor({
      plugins: [
        BaseColumnPlugin,
        BaseColumnItemPlugin,
        BaseTocPlugin,
        BaseVideoPlugin,
        BaseAudioPlugin,
        BaseParagraphPlugin,
        BaseHeadingPlugin,
        BaseMediaEmbedPlugin,
        BaseBoldPlugin,
        BaseCodePlugin,
        BaseItalicPlugin,
        BaseStrikethroughPlugin,
        BaseSubscriptPlugin,
        BaseSuperscriptPlugin,
        BaseUnderlinePlugin,
        BaseBlockquotePlugin,
        BaseDatePlugin,
        BaseEquationPlugin,
        BaseInlineEquationPlugin,
        BaseCodeBlockPlugin.configure({
          options: {
            prism: Prism,
          },
        }),
        BaseIndentPlugin.extend({
          inject: {
            targetPlugins: [
              BaseParagraphPlugin.key,
              BaseBlockquotePlugin.key,
              BaseCodeBlockPlugin.key,
            ],
          },
        }),
        BaseIndentListPlugin.extend({
          inject: {
            targetPlugins: [
              BaseParagraphPlugin.key,
              ...HEADING_LEVELS,
              BaseBlockquotePlugin.key,
              BaseCodeBlockPlugin.key,
              BaseTogglePlugin.key,
            ],
          },
        }),
        BaseLinkPlugin,
        BaseTableRowPlugin,
        BaseTablePlugin,
        BaseTableCellPlugin,
        BaseHorizontalRulePlugin,
        BaseFontColorPlugin,
        BaseFontBackgroundColorPlugin,
        BaseFontSizePlugin,
        BaseKbdPlugin,
        BaseAlignPlugin.extend({
          inject: {
            targetPlugins: [
              BaseParagraphPlugin.key,
              BaseMediaEmbedPlugin.key,
              ...HEADING_LEVELS,
              BaseImagePlugin.key,
            ],
          },
        }),
        BaseLineHeightPlugin,
        BaseHighlightPlugin,
        BaseFilePlugin,
        BaseImagePlugin,
        BaseMentionPlugin,
        BaseCommentsPlugin,
        BaseTogglePlugin,
      ],
      value: editorContent,
    });

    // Generate HTML using serializeHtml (same as export-toolbar-button)
    const html = await serializeHtml(editorStatic, {
      components,
      editorComponent: EditorStatic,
      props: { style: { paddingBottom: "" } },
    });

    return html;
  } catch (error) {
    console.error("Error generating HTML from editor content:", error);
    return "";
  }
}

/**
 * Parses jsonBody back to tree and html
 * @param jsonBody - Stringified JSON containing tree and html
 * @returns Object with tree and html properties
 */
export function parseComponentJsonBody(jsonBody: string): {
  tree: any[];
  html: string;
} {
  try {
    const parsed = JSON.parse(jsonBody);
    return {
      tree: parsed.tree || [],
      html: parsed.html || "",
    };
  } catch (error) {
    console.error("Error parsing jsonBody:", error);
    return {
      tree: [],
      html: "",
    };
  }
}

/**
 * Converts Component format back to internal format (for reading in CoverComponent and Content)
 * @param component - Component object in the new format
 * @returns Object with pagesList and coverDataList in the old format
 */
export function convertComponentToInternalFormat(component: any): {
  pagesList: Array<{ id: string; title: string; data: any[] }>;
  coverDataList: any[];
} {
  // Parse jsonBody to get tree
  const jsonBodyData = parseComponentJsonBody(component.jsonBody);
  const tree = jsonBodyData.tree || [];

  // Convert tree back to pagesList format
  const pagesList = tree.map((page: any) => ({
    id: page.id || String(Math.random()),
    title: page.title || "",
    data: page.data || [],
  }));

  // Helper to generate UUID
  const generateUUID = () => {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback for Node.js environments
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  };

  // Reconstruct coverDataList from Component fields - only 3 nodes: img, header, subHeader
  const coverDataList = [
    {
      id: generateUUID(),
      type: "img",
      children: [],
      properties: {
        content: component.imageUrl || "",
      },
    },
    {
      id: generateUUID(),
      type: "header",
      children: [],
      properties: {
        content: component.title || "",
      },
    },
    {
      id: generateUUID(),
      type: "subHeader",
      children: [],
      properties: {
        content: component.description || "",
      },
    },
  ];

  return { pagesList, coverDataList };
}

/**
 * Transforms old proposal format to Component format (for propData.json migration)
 * @param oldProposal - Old proposal format with content and cover arrays
 * @param componentId - Component ID (defaults to 0)
 * @returns Component object in the new format
 */
export function transformOldProposalToComponent(
  oldProposal: any,
  componentId: number = 0
): any {
  const cover = oldProposal.cover || [];
  const content = oldProposal.content || [];

  // Extract cover data
  const coverImageNode = cover.find((node: any) => node.type === "img");
  const headerNode = cover.find((node: any) => node.type === "header");
  const subHeaderNode = cover.find((node: any) => node.type === "subHeader");

  const imageUrl = coverImageNode?.properties?.content || "";
  const title = headerNode?.properties?.content || oldProposal.title || "";
  const description = subHeaderNode?.properties?.content || "";

  // Build tree structure from content (pagesList format)
  const tree = content.map((page: any) => ({
    id: page.id || String(Math.random()),
    title: page.title || "",
    data: page.data || [],
  }));

  // Create jsonBody with tree and empty html (will be generated on export)
  const jsonBodyData = {
    tree,
    html: "", // HTML will be generated when export button is clicked
  };

  // Extract media from cover and content
  const media: any[] = [];

  // Add cover image to media
  if (coverImageNode?.properties?.content) {
    media.push({
      id: media.length + 1,
      componentId,
      url: coverImageNode.properties.content,
      mediaType: "image",
      image: coverImageNode.properties.content,
    });
  }

  // Extract images from content
  content.forEach((page: any) => {
    if (page.data && Array.isArray(page.data)) {
      page.data.forEach((element: any) => {
        if (element.type === "img" && element.content?.url) {
          media.push({
            id: media.length + 1,
            componentId,
            url: element.content.url,
            mediaType: "image",
            image: element.content.url,
          });
        } else if (
          element.type === "editor" &&
          Array.isArray(element.content)
        ) {
          element.content.forEach((node: any) => {
            if (node.type === "img" && node.url) {
              media.push({
                id: media.length + 1,
                componentId,
                url: node.url,
                mediaType: "image",
                image: node.url,
              });
            }
          });
        }
      });
    }
  });

  return {
    id: componentId,
    lang: "en",
    parentId: 0,
    title,
    note: "",
    description,
    jsonBody: JSON.stringify(jsonBodyData),
    url: "",
    imageUrl,
    displayOrder: 0,
    media,
    image: imageUrl,
    // Keep old fields for backward compatibility during migration
    createdAt: oldProposal.createdAt,
    updatedAt: oldProposal.updatedAt,
    status: oldProposal.status,
  };
}

export interface BlockTemplate {
  id: string;
  name: string;
  type: string;
  content?: any;
  createdAt: string;
  onSave?: (block: BlockTemplate) => void;
  onDelete?: (id: string) => void;
  onEdit?: (blockId: string) => void;
}
